import type { CSSProperties, ReactNode } from 'react'

import { useCallback, useEffect, useRef, useState } from 'react'
import ArtFrame from '@/components/generals/art-frame'
import LineReveal from '@/components/generals/line-reveal'
import TransitionLink from '@/components/generals/transition-link'
import { SIGNATURE_DISHES } from '@/constants/home'

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max)

const pad = (value: number): string => String(value).padStart(2, '0')

const Signatures = (): ReactNode => {
    const section = useRef<HTMLElement>(null)
    const sticky = useRef<HTMLDivElement>(null)
    const track = useRef<HTMLDivElement>(null)
    const items = useRef<Array<HTMLElement | null>>([])
    const [current, setCurrent] = useState(0)

    useEffect(() => {
        const sectionElement = section.current
        const stickyElement = sticky.current
        const trackElement = track.current

        if (!sectionElement || !stickyElement || !trackElement) return

        const desktop = window.matchMedia('(min-width: 1024px)')
        let frame = 0
        let gap = 0

        const measure = () => {
            gap = parseFloat(window.getComputedStyle(trackElement).columnGap) || 0
        }

        const update = () => {
            frame = 0

            if (desktop.matches) {
                const rect = sectionElement.getBoundingClientRect()
                const total = rect.height - window.innerHeight
                const progress = total > 0 ? clamp(-rect.top / total, 0, 1) : 0
                const distance = Math.max(trackElement.scrollWidth - stickyElement.clientWidth, 0)

                trackElement.style.transform = `translate3d(${(-progress * distance).toFixed(2)}px, 0, 0)`
            }
            else {
                trackElement.style.transform = ''
            }

            const middle = window.innerWidth / 2
            let closest = 0
            let best = Number.POSITIVE_INFINITY

            items.current.forEach((item, index) => {
                if (!item || item.offsetParent === null) return

                const box = item.getBoundingClientRect()
                const distance = Math.abs(box.left + box.width / 2 - middle) / (item.offsetWidth + gap)

                item.style.setProperty('--t', clamp(1 - distance, 0, 1).toFixed(3))

                if (distance < best) {
                    best = distance
                    closest = index
                }
            })

            setCurrent(closest)
        }

        const schedule = () => {
            if (!frame) frame = window.requestAnimationFrame(update)
        }

        const onResize = () => {
            measure()
            schedule()
        }

        measure()
        update()

        window.addEventListener('scroll', schedule, { passive: true })
        window.addEventListener('resize', onResize, { passive: true })
        trackElement.addEventListener('scroll', schedule, { passive: true })
        desktop.addEventListener('change', onResize)

        return () => {
            window.removeEventListener('scroll', schedule)
            window.removeEventListener('resize', onResize)
            trackElement.removeEventListener('scroll', schedule)
            desktop.removeEventListener('change', onResize)
            if (frame) window.cancelAnimationFrame(frame)
        }
    }, [])

    const bringIntoView = useCallback((position: number) => {
        const sectionElement = section.current
        const stickyElement = sticky.current

        if (!sectionElement || !stickyElement) return
        if (!window.matchMedia('(min-width: 1024px)').matches) return

        const total = sectionElement.offsetHeight - window.innerHeight
        const top = window.scrollY + sectionElement.getBoundingClientRect().top
        const steps = SIGNATURE_DISHES.length

        stickyElement.scrollLeft = 0
        window.scrollTo({ top: top + (position / steps) * total })
    }, [])

    return (
        <section
            id='signatures'
            ref={section}
            aria-labelledby='signatures-title'
            className='signature-story relative'
            style={{ '--steps': SIGNATURE_DISHES.length } as CSSProperties}
        >
            <div ref={sticky} className='signature-sticky relative flex flex-col justify-center py-24 md:py-32 lg:py-0'>
                <h2 id='signatures-title' className='sr-only'>Signatures: five plates from the fire</h2>

                <div aria-hidden='true' className='shell lg:hidden'>
                    <p className='caps text-brass'>Signatures</p>

                    <LineReveal
                        as='p'
                        lines={["Five plates", <em key='fire'>from the fire.</em>]}
                        className='mt-6 font-heading text-[clamp(2.6rem,9vw,4.5rem)] leading-[0.95] tracking-[-0.03em]'
                    />
                </div>

                <div
                    ref={track}
                    className='signature-track no-scrollbar mt-4 flex snap-x snap-mandatory items-center gap-5 overflow-x-auto px-[11vw] py-10 md:px-[18vw] lg:mt-0 lg:py-0'
                >
                    <div
                        ref={element => {
                            items.current[0] = element
                        }}
                        className='signature-frame hidden shrink-0 flex-col justify-center lg:flex'
                    >
                        <p aria-hidden='true' className='caps text-brass'>Signatures</p>

                        <p aria-hidden='true' className='mt-8 font-heading text-[clamp(2.75rem,4.4vw,5.25rem)] leading-[0.92] tracking-[-0.03em]'>
                            Five plates <em className='block'>from the fire.</em>
                        </p>

                        <p className='mt-8 max-w-[30ch] text-[0.95rem] leading-relaxed text-ivory/60'>
                            Keep scrolling. Each one is cooked over charcoal or oak, and each one is on the menu tonight.
                        </p>
                    </div>

                    {SIGNATURE_DISHES.map((dish, index) => (
                        <article
                            key={dish.name}
                            ref={element => {
                                items.current[index + 1] = element
                            }}
                            className='signature-frame w-[78vw] max-w-[420px] shrink-0 snap-center md:w-[56vw] lg:max-w-none'
                        >
                            <TransitionLink
                                href='/menu/feu'
                                aria-label={`${dish.name}, ${dish.price}. See it on the menu`}
                                onFocus={() => bringIntoView(index + 1)}
                                className='group block'
                            >
                                <div className='relative'>
                                    <div
                                        aria-hidden='true'
                                        className='signature-glow pointer-events-none absolute -inset-[16%] -z-10 bg-[radial-gradient(closest-side,rgb(201_171_124/0.24),transparent)] blur-2xl'
                                    />

                                    <ArtFrame
                                        caption={dish.image.caption}
                                        alt={dish.image.alt}
                                        src={dish.image.src}
                                        imageClassName={dish.image.imageClassName}
                                        tone={dish.image.tone}
                                        motif={dish.image.motif}
                                        index={pad(index + 1)}
                                        sizes='(min-width: 1024px) 30vw, 78vw'
                                        className='aspect-[4/5] w-full transition-transform duration-1000 ease-film group-hover:scale-[1.01]'
                                    />
                                </div>

                                <div className='mt-6 flex items-start justify-between gap-6'>
                                    <div>
                                        <h3 className='font-heading text-[clamp(1.7rem,2.4vw,2.5rem)] leading-[1.02] tracking-[-0.015em] transition-colors duration-500 group-hover:text-brass-bright'>
                                            {dish.name}
                                        </h3>

                                        <p className='mt-3 max-w-[36ch] text-[0.95rem] leading-relaxed text-ivory/60'>
                                            {dish.description}
                                        </p>
                                    </div>

                                    <p className='pt-1 font-heading text-[1.6rem] leading-none tabular-nums text-ivory/90'>
                                        {dish.price}
                                    </p>
                                </div>
                            </TransitionLink>
                        </article>
                    ))}
                </div>

                <div className='shell mt-2 flex items-center justify-between lg:absolute lg:inset-x-0 lg:bottom-10 lg:mt-0'>
                    <p className='caps tabular-nums text-ivory/45' aria-hidden='true'>
                        {current === 0 ? 'Plates' : `Plate ${pad(current)}`} / {pad(SIGNATURE_DISHES.length)}
                    </p>

                    <TransitionLink href='/menu' className='link-line caps text-ivory/75'>
                        The full menu
                    </TransitionLink>
                </div>
            </div>
        </section>
    )
}

export default Signatures
